import { __assign, __read } from "tslib";
import { getFromColorMap, isGreyscale, isThemeColor, isColor } from '../color';
import { getShadow, getFilter } from './svgFilters';
import { parseSvgString } from './svgParser';
import Color from 'color';
var viewBoxMatcher = /viewBox="([^"]*)"/i;
var svgWidthMatcher = /(?:<svg[^>]*)\s(width="[^"]*")/i;
var svgHeightMatcher = /(?:<svg[^>]*)\s(height="[^"]*")/i;
var svgTagMatcher = /(<svg[^>]*)(>)/;
var fillAttributeMatcher = /fill="(.*?)"/gi;
var styleAttributeMatcher = /style="([^"]*)"/i;
var styleTransformMatcher = /transform:([^;]*)/i;
var SVG_TYPES = {
    SHAPE: 'shape',
    TINT: 'tint',
    COLOR: 'color',
    UGC: 'ugc',
};
function replaceSVGMaskAttributes(svgString, svgId) {
    if (!svgString) {
        return svgString;
    }
    var svgTagMatch = svgString.match(/<svg[^>]*>/);
    if (svgTagMatch) {
        var svgTag = svgTagMatch[0];
        var idMatch = svgTag.match(/id="[-\w]+"/);
        var aspectRatioMatch = svgTag.match(/preserveAspectRatio="[-\w\s]+"/);
        if (aspectRatioMatch) {
            svgTag = svgTag.replace(aspectRatioMatch[0], 'preserveAspectRatio="none"');
        }
        else {
            svgTag = svgTag.replace('<svg ', '<svg preserveAspectRatio="none" ');
        }
        if (idMatch) {
            svgTag = svgTag.replace(idMatch[0], "id=\"" + svgId + "\"");
        }
        else {
            svgTag = svgTag.replace('<svg ', "<svg id=\"" + svgId + "\" ");
        }
        svgString = svgString.replace(svgTagMatch[0], svgTag);
    }
    return svgString;
}
var symbols = /[\r\n%#()<>?[\\\]^`{|}]/g;
/**
 * Returns a data URI SVG encoded for CSS <url> value
 */
function encodeSVG(svg) {
    var escaped = svg
        .replace(/"/g, "'") // Use single quotes instead of double to avoid encoding.
        .replace(/>\s+</g, '><') // trim
        .replace(/\s{2,}/g, ' ') // trim
        .replace(symbols, encodeURIComponent); // encode symbols
    return "url(\"data:image/svg+xml," + escaped + "\")";
}
function getViewBoxString(_a) {
    var x = _a.x, y = _a.y, width = _a.width, height = _a.height;
    return x + " " + y + " " + width + " " + height;
}
function getViewBoxObject(viewBox) {
    var _a = __read(viewBox.split(' ').map(function (s) { return parseFloat(s); }), 4), x = _a[0], y = _a[1], width = _a[2], height = _a[3];
    return {
        x: x,
        y: y,
        width: width,
        height: height,
    };
}
function getScaledViewBox(boxBoundaries, strokeWidth, size, maintainAspectRatio) {
    var wScale = (size.width - strokeWidth) / boxBoundaries.width;
    var hScale = (size.height - strokeWidth) / boxBoundaries.height;
    var aspectScale = Math.min(wScale, hScale);
    var width = size.width / (maintainAspectRatio ? aspectScale : wScale);
    var height = size.height / (maintainAspectRatio ? aspectScale : hScale);
    var x = boxBoundaries.x - (width - boxBoundaries.width) / 2;
    var y = boxBoundaries.y - (height - boxBoundaries.height) / 2;
    return {
        width: width,
        height: height,
        x: x,
        y: y,
    };
}
function getScaledSvgWithStroke(strokeWidth, layout, bbox, preserveAspectRatio, svgString) {
    var viewBoxValue = strokeWidth && layout
        ? getViewBoxString(getScaledViewBox(getViewBoxObject(bbox), strokeWidth, layout, preserveAspectRatio))
        : bbox;
    var newViewBox = "viewBox=\"" + viewBoxValue + "\"";
    return svgString.replace(viewBoxMatcher, newViewBox);
}
function transformToTintColors(svgString, resolvedColor) {
    var baseColor = Color(resolvedColor);
    return svgString.replace(fillAttributeMatcher, function (__, colorToTint) {
        var colorObj = Color(colorToTint);
        if (isGreyscale(colorObj)) {
            var tint = 1 - (255 - colorObj.red()) / 255; // eslint-disable-line no-mixed-operators
            var rTint = Math.floor(baseColor.red() + (255 - baseColor.red()) * tint); // eslint-disable-line no-mixed-operators
            var gTint = Math.floor(baseColor.green() + (255 - baseColor.green()) * tint); // eslint-disable-line no-mixed-operators
            var bTint = Math.floor(baseColor.blue() + (255 - baseColor.blue()) * tint); // eslint-disable-line no-mixed-operators
            var tintedColor = Color({ r: rTint, g: gTint, b: bTint });
            // return tinted color
            return "fill=\"" + tintedColor.hex().toString() + "\"";
        }
        // no change, return original svg color
        return "fill=\"" + colorToTint + "\"";
    });
}
function removeWidthAndHeight(svgString) {
    if (svgString) {
        var widthMatch = svgString.match(svgWidthMatcher);
        var heightMatch = svgString.match(svgHeightMatcher);
        if (widthMatch && widthMatch.length > 1) {
            svgString = svgString.replace(widthMatch[1], 'width="100%"');
        }
        if (heightMatch && heightMatch.length > 1) {
            svgString = svgString.replace(heightMatch[1], 'height="100%"');
        }
    }
    return svgString;
}
function addA11yFeatures(svgString, compId, altText) {
    var newSvgString = svgString.replace(svgTagMatcher, '$1 role="img"$2');
    if (typeof altText === 'string') {
        var svgLabelId = compId + "-svgtitle";
        return newSvgString.replace(svgTagMatcher, "$1 aria-labelledby=\"" + svgLabelId + "\"$2<title id=\"" + svgLabelId + "\">" + altText + "</title>");
    }
    return newSvgString;
}
function getFilterRectInPixels(layout, shadow) {
    var width = layout.width, height = layout.height;
    var blurRadius = shadow.blurRadius, x = shadow.x, y = shadow.y;
    // STD to pixel ~ STD value * 3 , we are multiplying by 6 for both sides
    var blurSpread = blurRadius * 6;
    var filterAttr = {
        x: Math.min(0, x) - blurSpread / 2,
        y: Math.min(0, y) - blurSpread / 2,
        width: width + blurSpread + Math.abs(x),
        height: height + blurSpread + Math.abs(y),
    };
    return "x=\"" + filterAttr.x + "\" y=\"" + filterAttr.y + "\" width=\"" + filterAttr.width + "\" height=\"" + filterAttr.height + "\"";
}
function getShadowFilter(filterId, shadow, layout, hexColor, mergeGraphic) {
    var filterString = getShadow(filterId, __assign(__assign({}, shadow), { color: hexColor, mergeGraphic: mergeGraphic }));
    return filterString.replace(/<filter /, "<filter " + getFilterRectInPixels(layout, shadow) + " filterUnits=\"userSpaceOnUse\" ");
}
function getScaledSvgViewBox(svgString, svgInfo, properties, layout, strokeWidth) {
    if (properties === void 0) { properties = {}; }
    if (strokeWidth === void 0) { strokeWidth = 0; }
    if (svgString) {
        var svgType = svgInfo.svgType, viewBox = svgInfo.viewBox, bbox = svgInfo.bbox;
        var preserveViewBox = properties.preserveViewBox, displayMode = properties.displayMode;
        var preserveAspectRatio = displayMode !== 'stretch';
        var preserveAspectRatioString = preserveAspectRatio ? 'xMidYMid meet' : 'none';
        svgString = svgString.replace(/<svg/, "<svg preserveAspectRatio=\"" + preserveAspectRatioString + "\"");
        if (!svgString.match(viewBoxMatcher)) {
            svgString =
                svgType === SVG_TYPES.UGC
                    ? svgString.replace(/<svg/, "<svg viewBox=\"" + viewBox + "\"")
                    : svgString.replace(/<svg/, "<svg viewBox=\"" + bbox + "\"");
        }
        if (svgType !== SVG_TYPES.UGC && !preserveViewBox && bbox) {
            svgString = getScaledSvgWithStroke(strokeWidth, layout, bbox, preserveAspectRatio, svgString);
        }
    }
    return svgString;
}
function wrapWithShadow(_a) {
    var compId = _a.compId, svgString = _a.svgString, layout = _a.layout, colorsMap = _a.colorsMap, shadow = _a.shadow, _b = _a.shadowOnly, shadowOnly = _b === void 0 ? false : _b;
    if (shadow && shadow.color) {
        var filterId = compId + "-shadow";
        var hexColor = getFromColorMap(shadow.color, colorsMap);
        var shadowFilter = getShadowFilter(filterId, shadow, layout, hexColor, !shadowOnly);
        var modifiedContent = removeWidthAndHeight(svgString);
        return "\n          <svg height=\"100%\" width=\"100%\">\n              <defs>" + shadowFilter + "</defs>\n              <g filter=\"url(#" + filterId + ")\">\n                  " + modifiedContent + "\n              </g>\n          </svg>\n      ";
    }
    return svgString;
}
var FLIP_DATA_TO_TRANSFORM = {
    x: 'scale(-1, 1)',
    y: 'scale(1, -1)',
    xy: 'scale(-1, -1)',
    none: '',
};
function getFlippedSVG(svgString, flip) {
    var topSvg = svgString.match(svgTagMatcher);
    var transform = FLIP_DATA_TO_TRANSFORM[flip] || '';
    if (topSvg && transform) {
        var styleMatch = topSvg[0].match(styleAttributeMatcher);
        var replacement = "$1 style=\"transform: " + transform + ";\"$2";
        if (styleMatch) {
            var transformMatch = styleMatch[1].match(styleTransformMatcher);
            if (transformMatch) {
                replacement = topSvg[0].replace(styleMatch[0], styleMatch[0].replace(transformMatch[0], "transform: " + transform + " " + transformMatch[1]));
            }
            else {
                replacement = topSvg[0].replace(styleMatch[0], "style=\"transform: " + transform + "; " + styleMatch[1] + "\"");
            }
        }
        return svgString.replace(svgTagMatcher, replacement);
    }
    return svgString;
}
function getSvgContentForMask(svgString, svgInfo, flip, mode, colorsMap, compId, layout, shadow) {
    var content = getScaledSvgViewBox(svgString, svgInfo, { displayMode: 'stretch' }, layout, 0);
    // we must have the transform style inlined inside the SVG content
    var svg = getFlippedSVG(content, flip);
    if (mode === 'drop-shadow' && shadow && layout) {
        return wrapWithShadow({ compId: compId, svgString: svg, layout: layout, colorsMap: colorsMap, shadow: shadow, shadowOnly: true });
    }
    return svg;
}
function addSvgStyleNode(svgString, defsValue) {
    if (!svgString) {
        return svgString;
    }
    return svgString.replace(/(<svg[^>]*>)/, "$1<defs><style>" + defsValue + "</style></defs>");
}
var DEFAULT_COLOR = '#242323';
var pickValidColors = function (obj) {
    return Object.keys(obj).reduce(function (result, key) {
        var colorValue = obj[key];
        if (isColor(colorValue) || isThemeColor(colorValue)) {
            result[key] = colorValue;
        }
        return result;
    }, {});
};
var transformVectorImage = function (rawSvg, _a) {
    var altText = _a.altText, compId = _a.compId, strokeWidth = _a.strokeWidth, svgInfo = _a.svgInfo, properties = _a.properties, layout = _a.layout, shadow = _a.shadow, colorsMap = _a.colorsMap, overrideColorsInuput = _a.overrideColors;
    var overrideColors = pickValidColors(overrideColorsInuput || {});
    function withA11y(svgString) {
        return addA11yFeatures(svgString, compId, altText);
    }
    function withScaledViewBox(svgString) {
        return getScaledSvgViewBox(svgString, svgInfo, properties, layout, strokeWidth);
    }
    function withShadowFilter(svgString) {
        return wrapWithShadow({ compId: compId, svgString: svgString, layout: layout, colorsMap: colorsMap, shadow: shadow });
    }
    function withTintColors(svgString) {
        var rawColor = (overrideColors && overrideColors.color1) || DEFAULT_COLOR;
        var color = getFromColorMap(rawColor, colorsMap);
        return svgInfo.svgType === SVG_TYPES.TINT ? transformToTintColors(svgString, color) : svgString;
    }
    function withCssOverrides(svgString) {
        var shouldOverrideColors = svgInfo.svgType === SVG_TYPES.COLOR && Object.keys(overrideColors).length > 0;
        if (shouldOverrideColors) {
            var colors = Object.entries(overrideColors)
                .map(function (_a) {
                var _b = __read(_a, 2), colorName = _b[0], rawColor = _b[1];
                var resolvedColor = getFromColorMap(rawColor, colorsMap);
                var index = colorName.replace('color', '');
                return "#" + compId + " svg [data-color=\"" + index + "\"] {fill: " + resolvedColor + ";}";
            })
                .join('\n');
            return addSvgStyleNode(svgString, colors);
        }
        return svgString;
    }
    return withCssOverrides(withShadowFilter(withScaledViewBox(withTintColors(withA11y(rawSvg)))));
};
var skinNameToUri = function (skin) {
    var partsArr = skin.replace(/^.*\//, '').split('.');
    var version = partsArr[1] === 'v1' ? 1 : 2;
    var svgHash = partsArr[2].replace(/svg_/i, '');
    var svgName = partsArr[3];
    return svgHash + (version === 1 ? "_svgshape.v1." + svgName : '') + ".svg";
};
export { transformVectorImage, parseSvgString, removeWidthAndHeight, skinNameToUri, getFilter, getScaledSvgViewBox, replaceSVGMaskAttributes, getSvgContentForMask, encodeSVG, };
//# sourceMappingURL=index.js.map